import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Tool inter-linking v1 — cross-tool breadcrumb copy. Each entry's
 * `displayNameShort` is the button label (level-independent so the link
 * itself does not shift register); the `copy.body` is the level-resolved
 * tooltip carrying the pedagogical framing.
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md
 * Source memo: .claude/agent-memory/bowie/project_tool_interlinking_v1_scope.md
 */
export const CROSSLINK_LEXICON_ENTRIES: readonly LexiconEntry[] = [
  {
    id: 'crosslink-mode-to-quiz',
    category: 'crosslink',
    displayName: 'Test functions in this key',
    displayNameShort: 'To Function Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Test your ear on which chord does what.',
      intermediate: 'Practice identifying chord function in this key.',
      advanced: 'Quiz yourself on tonic / predominant / dominant attribution in this tonal context.',
    },
    tagLabel: { beginner: 'next: quiz', intermediate: 'function quiz', advanced: 'function attribution' },
  },
  {
    id: 'crosslink-mode-to-reharm',
    category: 'crosslink',
    displayName: 'Compose a progression in this key',
    displayNameShort: 'To Reharm Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Write a chord progression and try swapping chords.',
      intermediate: 'Build a progression in this key and explore substitutions.',
      advanced: 'Apply reharmonization techniques to a progression in this key.',
    },
    tagLabel: { beginner: 'next: compose', intermediate: 'reharm lab', advanced: 'reharmonization' },
  },
  {
    id: 'crosslink-quiz-to-mode',
    category: 'crosslink',
    displayName: 'Explore modes from this key',
    displayNameShort: 'To Mode Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear how the same starting note sounds in different modes.',
      intermediate: 'Explore the four modes parallel to this tonic.',
      advanced: 'Compare modal alterations against the parallel reference at this tonic.',
    },
    tagLabel: { beginner: 'next: modes', intermediate: 'mode lab', advanced: 'modal exploration' },
  },
  {
    id: 'crosslink-quiz-to-reharm',
    category: 'crosslink',
    displayName: 'Apply these functions in a progression',
    displayNameShort: 'To Reharm Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Now try writing your own progression using these chords.',
      intermediate: 'Apply tonic / predominant / dominant motion in a progression.',
      advanced: 'Compose with the functions you just practiced — substitute and audition.',
    },
    tagLabel: { beginner: 'next: write', intermediate: 'reharm lab', advanced: 'apply functions' },
  },
  {
    id: 'crosslink-reharm-to-mode',
    category: 'crosslink',
    displayName: 'Hear the modes of this key',
    displayNameShort: 'To Mode Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear what your key sounds like in different modes.',
      intermediate: 'Explore the four modes parallel to this tonic.',
      advanced: 'Compare modal characteristic notes at this tonic.',
    },
    tagLabel: { beginner: 'next: modes', intermediate: 'mode lab', advanced: 'modal context' },
  },
  {
    id: 'crosslink-reharm-to-quiz',
    category: 'crosslink',
    displayName: 'Test functions in this key',
    displayNameShort: 'To Function Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Test your ear on which chord does what in this key.',
      intermediate: 'Practice identifying chord function in this tonal context.',
      advanced: 'Quiz yourself on tonic / predominant / dominant in this key.',
    },
    tagLabel: { beginner: 'next: quiz', intermediate: 'function quiz', advanced: 'function attribution' },
  },
  {
    id: 'crosslink-borrowed-flat-VII-to-mode',
    category: 'crosslink',
    displayName: '♭VII comes from Mixolydian',
    displayNameShort: 'Hear in Mixolydian',
    pedagogicalLevel: 2,
    copy: {
      beginner: '♭VII is borrowed from Mixolydian — hear the mode it comes from.',
      intermediate: 'Borrowed ♭VII originates in Mixolydian — explore the parent mode.',
      advanced: 'The ♭7 scale degree is the Mixolydian signature — hear the full modal context.',
    },
    tagLabel: { beginner: 'mixolydian source', intermediate: 'mixolydian context', advanced: '♭7 source mode' },
  },
  // v1.1 — Cadence Lab + Melody Writer standalone routes (Bowie-locked, verbatim from memo).
  // Spec: docs/superpowers/specs/2026-05-20-drawer-tools-standalone-routes-design.md §6
  {
    id: 'crosslink-cadence-to-mode',
    category: 'crosslink',
    displayName: 'Explore modes in this key',
    displayNameShort: 'To Mode Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear how this key sounds in different modes.',
      intermediate: 'Explore the modes parallel to this tonic.',
      advanced: 'Compare modal characteristic notes at this tonic.',
    },
    tagLabel: { beginner: 'next: modes', intermediate: 'mode lab', advanced: 'modal context' },
  },
  {
    id: 'crosslink-cadence-to-quiz',
    category: 'crosslink',
    displayName: 'Test functions in this key',
    displayNameShort: 'To Function Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Test your ear on which chord does what in this key.',
      intermediate: 'Practice identifying chord function in this tonal context.',
      advanced: 'Quiz yourself on tonic / predominant / dominant in this key.',
    },
    tagLabel: { beginner: 'next: quiz', intermediate: 'function quiz', advanced: 'function attribution' },
  },
  {
    id: 'crosslink-melody-to-cadence',
    category: 'crosslink',
    displayName: 'Pick a cadence for this progression',
    displayNameShort: 'To Cadence Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Try different endings for your chord progression.',
      intermediate: 'Compare cadence endings for this progression.',
      advanced: 'Audition authentic / plagal / half / deceptive cadences over this progression.',
    },
    tagLabel: { beginner: 'next: endings', intermediate: 'cadence lab', advanced: 'cadence audition' },
  },
  {
    id: 'crosslink-melody-to-reharm',
    category: 'crosslink',
    displayName: 'Reharmonize this progression',
    displayNameShort: 'To Reharm Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Try swapping chords in your progression.',
      intermediate: 'Explore substitutions for this progression.',
      advanced: 'Apply reharmonization techniques to this progression.',
    },
    tagLabel: { beginner: 'next: reharm', intermediate: 'reharm lab', advanced: 'reharmonization' },
  },
  // ───────── Ear Training Suite v1 outbound cross-links (4 entries) ─────────
  {
    id: 'crosslink-ear-to-quiz',
    category: 'crosslink',
    displayName: 'Function Quiz',
    displayNameShort: 'To Function Quiz',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Test which chord does what in a key — function quiz.',
      intermediate: 'Practice identifying chord function in a key context.',
      advanced: 'Quiz yourself on tonic / predominant / dominant attribution.',
    },
    tagLabel: { beginner: 'function quiz', intermediate: 'function quiz', advanced: 'function quiz' },
  },
  {
    id: 'crosslink-ear-to-mode',
    category: 'crosslink',
    displayName: 'Mode Lab',
    displayNameShort: 'To Mode Lab',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Hear how modes change the colour of these notes.',
      intermediate: 'Explore how these intervals / qualities live in different modes.',
      advanced: 'Compare these structural elements across the seven modes.',
    },
    tagLabel: { beginner: 'modes', intermediate: 'mode lab', advanced: 'mode lab' },
  },
  {
    id: 'crosslink-ear-to-reharm',
    category: 'crosslink',
    displayName: 'Reharm Lab',
    displayNameShort: 'To Reharm Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Write a progression using these chords.',
      intermediate: 'Build a progression and explore substitutions for these qualities.',
      advanced: 'Apply reharmonization to these qualities in a tonal context.',
    },
    tagLabel: { beginner: 'reharm', intermediate: 'reharm lab', advanced: 'reharm lab' },
  },
  {
    id: 'crosslink-ear-to-cadence',
    category: 'crosslink',
    displayName: 'Cadence Lab',
    displayNameShort: 'To Cadence Lab',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Hear cadences — the way songs end.',
      intermediate: 'Compare cadence endings in a key.',
      advanced: 'Audition authentic / plagal / half / deceptive cadences.',
    },
    tagLabel: { beginner: 'cadences', intermediate: 'cadence lab', advanced: 'cadence lab' },
  },
  // ───────── v1.1 inter-linking matrix completion (3 entries) ─────────
  // Bowie-locked top-3 pedagogical gaps from the v1 link matrix.
  {
    id: 'crosslink-mode-to-cadence',
    category: 'crosslink',
    displayName: 'Cadence Lab',
    displayNameShort: 'To Cadence Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: "Hear how this mode's signature chord lands in a cadence.",
      intermediate: 'Compare cadence endings within this mode.',
      advanced: 'Audition cadences in this modal context — modal-no-leading-tone suppression applies.',
    },
    tagLabel: { beginner: 'next: cadences', intermediate: 'cadence lab', advanced: 'modal cadence' },
  },
  {
    id: 'crosslink-quiz-to-ear',
    category: 'crosslink',
    displayName: 'Ear Training',
    displayNameShort: 'To Ear Training',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Train your ear on intervals and chord qualities.',
      intermediate: 'Sharpen interval + chord-quality identification by ear.',
      advanced: 'Practice structural ear-training — interval ID and chord-quality ID — independent of tonal context.',
    },
    tagLabel: { beginner: 'next: ear training', intermediate: 'ear training', advanced: 'ear ID' },
  },
  {
    id: 'crosslink-reharm-to-cadence',
    category: 'crosslink',
    displayName: 'Cadence Lab',
    displayNameShort: 'To Cadence Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Try different endings after your substitution.',
      intermediate: 'Compare cadence endings for this progression after applying substitutions.',
      advanced: 'Audition cadence endings on the reharmonized progression.',
    },
    tagLabel: { beginner: 'next: cadences', intermediate: 'cadence lab', advanced: 'cadence audition' },
  },
  // ───────── Rhythm Trainer v1 cross-links (4 entries) ─────────
  // Outbound — from the Rhythm Trainer footer to sibling tools:
  {
    id: 'crosslink-rhythm-to-ear',
    category: 'crosslink',
    displayName: 'Ear Training',
    displayNameShort: 'To Ear Training',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Train your ear for pitch — intervals and chord qualities.',
      intermediate: 'Identify intervals and chord qualities by ear — the pitch counterpart to rhythm training.',
      advanced: 'Audition interval and chord-quality identification alongside metric training.',
    },
    tagLabel: { beginner: 'ear training', intermediate: 'ear training', advanced: 'ear training' },
  },
  {
    id: 'crosslink-rhythm-to-quiz',
    category: 'crosslink',
    displayName: 'Function Quiz',
    displayNameShort: 'To Function Quiz',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Test which chord does what in a key — function quiz.',
      intermediate: 'Practice identifying chord function in a key context.',
      advanced: 'Quiz yourself on tonic / predominant / dominant attribution.',
    },
    tagLabel: { beginner: 'function quiz', intermediate: 'function quiz', advanced: 'function quiz' },
  },
  {
    id: 'crosslink-rhythm-to-mode',
    category: 'crosslink',
    displayName: 'Mode Lab',
    displayNameShort: 'To Mode Lab',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Hear how modes change the colour of a scale.',
      intermediate: 'Explore how scale degrees recolour across the seven modes.',
      advanced: 'Compare modal colour against the parallel reference.',
    },
    tagLabel: { beginner: 'modes', intermediate: 'mode lab', advanced: 'mode lab' },
  },
  // Inbound — used by a future Ear Training → Rhythm footer link revision:
  {
    id: 'crosslink-ear-to-rhythm',
    category: 'crosslink',
    displayName: 'Rhythm Trainer',
    displayNameShort: 'To Rhythm Trainer',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Train your ear for time signatures and rhythmic feel.',
      intermediate: 'Identify meters and subdivisions by ear — the rhythmic counterpart.',
      advanced: 'Audition metric grouping and subdivision feel.',
    },
    tagLabel: { beginner: 'rhythm', intermediate: 'rhythm trainer', advanced: 'rhythm trainer' },
  },
  // ───────── Form Visualizer v1 cross-links (3 entries) ─────────
  // Outbound — from the Form Visualizer footer to sibling tools + suite home:
  {
    id: 'crosslink-form-to-cadence',
    category: 'crosslink',
    displayName: 'Cadence Lab',
    displayNameShort: 'To Cadence Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear how phrases end — cadences are how sections close.',
      intermediate: 'Cadences signal section boundaries — explore them in Cadence Lab.',
      advanced: 'Cadence shape is half of form perception — phrase endings mark section boundaries.',
    },
    tagLabel: { beginner: 'phrase endings', intermediate: 'cadence lab', advanced: 'cadence audition' },
  },
  {
    id: 'crosslink-form-to-rhythm',
    category: 'crosslink',
    displayName: 'Rhythm Trainer',
    displayNameShort: 'To Rhythm Trainer',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Train your ear for the pulse beneath song sections.',
      intermediate: 'Meter and subdivision — the beat-level pulse that sections sit on.',
      advanced: 'Rhythmic perception complements form perception — beat below, structure above.',
    },
    tagLabel: { beginner: 'rhythm', intermediate: 'rhythm trainer', advanced: 'metric training' },
  },
  {
    id: 'crosslink-form-to-suite',
    category: 'crosslink',
    displayName: 'Music Theory Suite',
    displayNameShort: 'Back to suite',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Back to all the music-theory tools.',
      intermediate: 'Back to the Music-Theory Learning Suite dashboard.',
      advanced: 'Back to the suite landing — all chord, scale, rhythm, and form tools.',
    },
    tagLabel: { beginner: 'suite', intermediate: 'suite home', advanced: 'suite dashboard' },
  },
  // ───────── Circle of Fifths Wheel v1 cross-links (3 entries) ─────────
  // Outbound — from the CoF Wheel footer to sibling tools + suite home:
  {
    id: 'crosslink-cof-to-mode',
    category: 'crosslink',
    displayName: 'Mode Lab',
    displayNameShort: 'To Mode Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Explore how a single tonic feels in different modes — major, minor, and the in-between flavours.',
      intermediate: 'Mode Lab teaches modal colour at a fixed tonic — the colour dimension of tonal geography.',
      advanced:
        'Modal colour (fixed tonic) complements key relationships (across tonics) — together: tonal geography.',
    },
    tagLabel: { beginner: 'modes', intermediate: 'mode lab', advanced: 'modal palette' },
  },
  {
    id: 'crosslink-cof-to-quiz',
    category: 'crosslink',
    displayName: 'Function Quiz',
    displayNameShort: 'To Function Quiz',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Once you know the key, drill which chord does what inside it.',
      intermediate: 'Function Quiz drills chord function within a key — the inside-the-key counterpart.',
      advanced: 'Diatonic-function recognition complements key navigation — function within / geography across.',
    },
    tagLabel: { beginner: 'chord roles', intermediate: 'function quiz', advanced: 'function drill' },
  },
  {
    id: 'crosslink-cof-to-suite',
    category: 'crosslink',
    displayName: 'Music Theory Suite',
    displayNameShort: 'Back to suite',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Back to all the music-theory tools.',
      intermediate: 'Back to the Music-Theory Learning Suite dashboard.',
      advanced: 'Back to the suite landing — all chord, scale, rhythm, and form tools.',
    },
    tagLabel: { beginner: 'suite', intermediate: 'suite home', advanced: 'suite dashboard' },
  },
  // ───── Modulation Lab v1 outbound + inbound crosslinks (Phase 4 Tier 3) ─────
  {
    id: 'crosslink-modulation-to-cof',
    category: 'crosslink',
    displayName: 'See the geography behind these modulations',
    displayNameShort: 'CoF Wheel',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'See where these keys live on the Circle of Fifths.',
      intermediate: 'View the closely-related cluster on the Circle of Fifths Wheel.',
      advanced: 'Cross-reference these modulations against the tonal-geography map.',
    },
    tagLabel: { beginner: 'CoF Wheel', intermediate: 'CoF Wheel', advanced: 'CoF Wheel' },
  },
  {
    id: 'crosslink-modulation-to-reharm',
    category: 'crosslink',
    displayName: 'Try substitutions within each key',
    displayNameShort: 'Reharm Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Explore chord substitutions within a single key.',
      intermediate: 'Try reharmonization options on the source or destination key vamp.',
      advanced: 'Explore intra-key substitution techniques in the Reharm Lab.',
    },
    tagLabel: { beginner: 'Reharm Lab', intermediate: 'Reharm Lab', advanced: 'Reharm Lab' },
  },
  {
    id: 'crosslink-modulation-to-suite',
    category: 'crosslink',
    displayName: 'Music Theory Suite',
    displayNameShort: 'Back to suite',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Back to all the music-theory tools.',
      intermediate: 'Back to the Music-Theory Learning Suite dashboard.',
      advanced: 'Back to the suite landing — all chord, scale, rhythm, and form tools.',
    },
    tagLabel: { beginner: 'suite', intermediate: 'suite home', advanced: 'suite dashboard' },
  },
  {
    id: 'crosslink-cof-to-modulation',
    category: 'crosslink',
    displayName: 'Hear how songs travel between these keys',
    displayNameShort: 'Modulation Lab',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Hear how songs change between these nearby keys.',
      intermediate: 'Audition the four canonical modulation techniques between closely-related keys.',
      advanced: "Explore cross-key motion — the journey complement to the wheel's geography.",
    },
    tagLabel: {
      beginner: 'Modulation Lab',
      intermediate: 'Modulation Lab',
      advanced: 'Modulation Lab',
    },
  },
]
